import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../elements/Navbar';
import Form from '../components/Form';
import InputField from '../components/InputField';
import ResponseBlock from '../components/ResponseBlock';
import { BeatLoader } from 'react-spinners';

function TagUpdater() {
  const [file, setFile] = useState(null);
  const [isTagEmpty, setIsTagEmpty] = useState(false);
  const [isTagCommandEmpty, setIsTagCommandEmpty] = useState(false);
  const [isFileEmpty, setIsFileEmpty] = useState(false);
  const [responseFileName, setResponseFileName] = useState('');
  const [fileName, setFileName] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [tagCommand, setTagCommand] = useState('');
  const [downloadLink, setDownloadLink] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setDownloadLink('');
    setMessage('');
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setFile(null);
    setIsFileEmpty(false);
    setIsTagEmpty(false);
    setIsTagCommandEmpty(false);
    setDownloadLink('');
    setMessage('');
    setFileName('');
    setTagValue('');
    setTagCommand('');
  };

  const handleTagValueChange = (event) => {
    setTagValue(event.target.value);
  };

  const handleTagCommandChange = (event) => {
    setTagCommand(event.target.value);
  };

  const handleFileNameChange = (event) => {
    setFileName(event.target.value);
  };

  const handleSubmit = async (event) => {
    const errors = [];
    setIsTagEmpty(false);
    setIsTagCommandEmpty(false);
    setIsFileEmpty(false);
    
    if (!tagValue) {
      setIsTagEmpty(true);
      errors.push('Please enter a tag.');
    };

    if (!tagCommand) {
      setIsTagCommandEmpty(true);
      errors.push('Please select an action.');
    };

    if (!file) {
      setIsFileEmpty(true);
      errors.push('Please select a file before submitting.');
    };

    if (errors.length > 0) {
      setMessage(errors.join(' '));
      return;
    }

    setIsLoading(true);
    setDownloadLink('');
    setMessage('');
    setResponseFileName('');
    const formData = new FormData();
    
    formData.append('file', file);
    formData.append('tag_value', tagValue);
    formData.append('tag_command', tagCommand);
    formData.append('file_name', fileName);

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL_BASE_DEV : process.env.REACT_APP_API_URL_BASE_PROD;
      const response = await axios.post(`${baseUrl}/csv_processor/matrixify-tag-csv-creator`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'];
      
      if (contentType && contentType.includes('application/json')) {
        // Convert the blob to text and parse it as JSON to handle the error
        const text = await response.data.text();
        const errorJson = JSON.parse(text);
        setMessage(`Error: ${errorJson.error}`);
      } else {
        setTimeout(() => {
          // Create a download link for the updated CSV
          const url = window.URL.createObjectURL(new Blob([response.data]));
          
          // Set the state with the filename and download link
          setResponseFileName(response.headers['x-filename']);
          setDownloadLink(url);
          setMessage('Your converted file is ready for download.');
        }, 1500);
      }
    } catch (error) {
      console.error("There was an error uploading the file!", error);
      setMessage('An error occurred while uploading the file.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1450)
    }
  };

  return(
    <div>
      <Navbar />
      <div className="container mx-auto">
        <div className="updaterWrapper">
          <div className="space-y-12">
            <h1 className="text-2xl font-semibold leading-7 text-gray-900">Introduction</h1>
            <p className="mt-1 leading-6" style={{marginTop: "1rem"}}>The purpose of the Tag Updater App is to create a CSV that can be used in Matrixify to either MERGE or DELETE tags from your Shopify store. This app is useful for bulk updating tags on products.
            </p>
            <div className="border-b border-gray-900/10 pb-12">
              <h2 className="text-base font-semibold leading-7 text-gray-900">Upload CSV from Matrixify</h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                The Tag Updater App expects a CSV file with the appropriate columns. If you are unaware of what columns are needed check out the Tutorial page for more information.
              </p>
            </div>
          </div>
          <div className="formWrapper mt-11">
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-6 border-b border-gray-900/10 pb-12 space-y-2">
                <div className="col-start-3 col-span-2">
                  <InputField 
                    label="Tag" 
                    type="text" 
                    name="tagValue"
                    error={isTagEmpty}
                    onChange={handleTagValueChange} 
                    placeholder="Tag"
                  />
                </div>
                <div className="col-start-3 col-span-2">
                  <InputField 
                    label="Choose an action" 
                    type="select" 
                    name="tagCommand" 
                    error={isTagCommandEmpty}
                    onChange={handleTagCommandChange}
                    options={['Select', 'ADD', 'REMOVE']}
                  />
                </div>
                <div className="col-start-3 col-span-2">
                  <InputField 
                    label="File Name (optional)" 
                    type="text" 
                    name="fileName" 
                    onChange={handleFileNameChange}
                    placeholder="If left empty, will default to todays date."
                  />
                </div>
                <div className="col-start-3 col-span-2 relative">
                  <InputField 
                    label="Choose a file" 
                    type="file" 
                    name="csv_file" 
                    error={isFileEmpty}
                    onChange={handleFileChange}
                    classes="w-full text-sm text-slate-500 p-3 outline-0
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-violet-50 file:text-violet-700
                            hover:file:bg-violet-100 mb-4"
                  />
                </div>
                <div className="col-end-5 col-span-1 text-right space-x-3">
                  <button className="text-rose-700 font-semibold py-2 px-4 rounded" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="w-1/2 bg-indigo-500 text-white font-bold py-2 px-4 rounded-lg" 
                          type="submit"
                          disabled={isLoading}>
                    { isLoading ? <BeatLoader /> : 'Submit' }
                  </button>
                </div>
              </div>
            </Form>
          </div>
          <div className="responseWrapper">
            <ResponseBlock 
              message={message} 
              link={downloadLink} 
              fileName={responseFileName}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TagUpdater;